import { Col, Row, Typography } from "antd";
import { useTranslation } from "react-i18next";

import colors from "../../../../../themes/colors";
import { formatDate } from "../../utils";
import { EMPTY_INDICATOR } from "../TradeTaxDeclaration/constants";
import {
  Questionnaire,
  QuestionnaireStatus as QuestionnaireStatusType,
} from "../../../../../api/graphql/schema.generated";

const { Text } = Typography;

export interface QuestionnaireDetailedStatusProps {
  questionnaire?: Questionnaire;
}

const QuestionnaireDetailedStatus = ({
  questionnaire,
}: QuestionnaireDetailedStatusProps) => {
  const { t } = useTranslation("TaxCaseOverview", {
    keyPrefix: "questionnaireStatus",
  });

  const date = (value?: string | null) => formatDate(value) ?? EMPTY_INDICATOR;

  // Documents required but missing: open. Without the flag (old cached data)
  // show the date or a dash.
  const documentsValue = ({
    documentsRequired,
    documentsUploadedAt,
  }: Questionnaire) =>
    documentsRequired && !documentsUploadedAt
      ? t("documentsOpen")
      : date(documentsUploadedAt);

  // Nothing to upload: the whole line says so, even if the backend set the
  // date at completion.
  const documentsLine = (q: Questionnaire) =>
    q.documentsRequired === false
      ? t("documentsNotRequired")
      : t("documentsComplete", { date: documentsValue(q) });

  if (!questionnaire) {
    return (
      <Col>
        <Row>
          <Text style={{ color: colors.lightGreyOpacity }}>{t("open")}</Text>
        </Row>
      </Col>
    );
  }

  // No fallbacks between the dates: an empty date means that step is open.
  const color =
    questionnaire.status === QuestionnaireStatusType.DOCUMENTS_UPLOADED
      ? colors.darkLimeGreen
      : colors.lightGreyOpacity;
  const lines = [
    t("started", { date: date(questionnaire.startedAt) }),
    t("answersSubmitted", { date: date(questionnaire.completedAt) }),
    documentsLine(questionnaire),
  ];

  return (
    <Col>
      {lines.map((line) => (
        <Row key={line}>
          <Text style={{ color }}>{line}</Text>
        </Row>
      ))}
    </Col>
  );
};

export default QuestionnaireDetailedStatus;
